import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { paymentButtonClass, PaymentModeButtons } from "../lib/paymentMethods";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Trash2, Receipt, Banknote, Smartphone, CreditCard, Lock, PackageSearch, Ban, PiggyBank, ShieldAlert, XCircle, Pencil, SlidersHorizontal, Plus } from "lucide-react";
import { useDocLock } from "../lib/locks";
import { LockBanner } from "../components/LockBanner";
import { ManagerAuthDialog } from "../components/ManagerAuthDialog";
import { ProductPicker } from "../components/ProductPicker";
import { CustomerPicker } from "../components/CustomerPicker";
import { ArticlesWindow } from "../components/ArticlesWindow";
import { InvoiceUiCustomizer } from "../components/InvoiceUiCustomizer";
import { useInvoiceUi } from "../hooks/useInvoiceUi";
import { makeMoney, FONT_CLASSES, DENSITY } from "../lib/invoiceUi";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "XOF", minimumFractionDigits: 0 }).format(amount || 0);
const toInputDate = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const parseNum = (v) => parseFloat(String(v).replace(",", "."));

const PAYMENT_METHODS = [
  { value: "especes", label: "ESPECE", icon: Banknote, primary: true },
  { value: "wave", label: "WAVE", icon: Smartphone, primary: true },
  { value: "orange_money", label: "ORANGE MONEY", icon: Smartphone, primary: true },
  { value: "cheque", label: "Chèque", icon: CreditCard },
  { value: "banque", label: "Banque", icon: CreditCard },
  { value: "non_paye", label: "NON PAYÉ", icon: Ban },
  { value: "avance", label: "Avance client", icon: PiggyBank },
];

const POSPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const { user, can, permissions, isManager, isAdmin } = useAuth();
  const { ui, update: updateUi, save: saveUi, reset: resetUi, revert: revertUi } = useInvoiceUi();
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [overdue, setOverdue] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);

  const canEditPrices = can("can_edit_prices");
  const maxDiscountPct = user?.role === "admin" ? 100 : permissions.max_discount_percent ?? 0;
  const pickerRef = useRef(null);
  const winRef = useRef(null);
  const linesRef = useRef(null);
  const qtyRefs = useRef({});
  const priceRefs = useRef({});
  const [focusTarget, setFocusTarget] = useState(null);
  const [winOpen, setWinOpen] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(() => toInputDate(new Date()));
  const [poRef, setPoRef] = useState("");
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("comptoir");
  const [paymentMethod, setPaymentMethod] = useState("especes");
  const [amountPaid, setAmountPaid] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [cashSession, setCashSession] = useState(undefined);
  const [editing, setEditing] = useState(null);
  const [depots, setDepots] = useState([]);
  const [depotId, setDepotId] = useState("");
  const [nextNumber, setNextNumber] = useState("");
  const [credit, setCredit] = useState(false);
  const [pending, setPending] = useState({ product: null, qty_raw: "", price_raw: "" });
  const [quickOpen, setQuickOpen] = useState(false);
  const [quick, setQuick] = useState({ name: "", phone: "" });
  const pendingQtyRef = useRef(null);
  const pendingPriceRef = useRef(null);
  const { lockedBy } = useDocLock("sale", editId);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [editId]);


  const fetchData = async () => {
    try {
      const [productsRes, customersRes, cashRes, depotsRes, nextRes] = await Promise.all([api.get("/products"), api.get("/customers"), api.get("/cash/session/current"), api.get("/depots").catch(() => ({ data: [] })), api.get("/sales/next-number").catch(() => ({ data: { number: "" } }))]);
      setProducts(productsRes.data);
      setCustomers(customersRes.data);
      setCashSession(cashRes.data.session);
      setDepots(depotsRes.data);
      setNextNumber(nextRes.data.number);
      if (!editId) setDepotId((d) => d || depotsRes.data[0]?.id || "");
      if (editId) {
        const sale = (await api.get(`/sales/${editId}`)).data;
        if (sale.status !== "active") { toast.error(sale.status === "cancelled" ? "Facture annulée : modification impossible" : "Facture avec retour : modification impossible"); navigate(`/invoice/${editId}`); return; }
        const byId = Object.fromEntries(productsRes.data.map((p) => [p.id, p]));
        setEditing(sale);
        setCart(sale.items.map((i) => {
          const p = byId[i.product_id];
          return { product_id: i.product_id, product_name: i.product_name, sku: p?.sku || "", unit: p?.unit || "", quantity: i.quantity, qty_raw: String(i.quantity), unit_price: i.unit_price, price_raw: String(i.unit_price), base_price: p?.price ?? i.unit_price, total: i.total, max_stock: (p?.stock || 0) + i.quantity };
        }));
        setSelectedCustomer(sale.customer_id || "comptoir");
        setDiscount(sale.discount || 0);
        setPaymentMethod(sale.payment_method);
        setInvoiceDate(toInputDate(sale.created_at));
        setPoRef(sale.purchase_order_ref || "");
        setDepotId(sale.depot_id || depotsRes.data[0]?.id || "");
      } else {
        setEditing(null);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
      setTimeout(() => pickerRef.current?.focus(), 50);
    }
  };

  useEffect(() => {
    if (!focusTarget) return;
    const el = focusTarget.field === "qty" ? qtyRefs.current[focusTarget.id] : focusTarget.field === "price" ? priceRefs.current[focusTarget.id] : focusTarget.field === "pqty" ? pendingQtyRef.current : focusTarget.field === "pprice" ? pendingPriceRef.current : null;
    if (el) { el.focus(); el.select(); }
    else if (focusTarget.field === "picker") pickerRef.current?.focus();
    else if (focusTarget.field === "list") winRef.current?.focus();
    setFocusTarget(null);
  }, [focusTarget, cart]);

  useEffect(() => {
    if (linesRef.current) linesRef.current.scrollTop = linesRef.current.scrollHeight;
  }, [cart.length]);

  const focusList = useCallback(() => {
    setWinOpen(true);
    setFocusTarget({ field: "list" });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F4") { e.preventDefault(); focusList(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusList]);

  const handleBarcode = async (code) => {
    try {
      const response = await api.get(`/products/barcode/${code}`);
      addToCart(response.data);
    } catch (error) {
      const product = products.find((p) => p.sku.toLowerCase() === code.toLowerCase() || p.barcode === code);
      if (product) addToCart(product); else toast.error("Produit non trouvé");
    }
  };

  const addToCart = (product, focusQty = false, qty = 1, price = null) => {
    const existing = cart.find((item) => item.product_id === product.id);
    const available = existing ? existing.max_stock : product.stock;
    if (available <= 0) { toast.error("Produit en rupture de stock"); return false; }
    const unitPrice = price !== null && !isNaN(price) ? price : product.price;
    if (existing) {
      if (existing.quantity + qty > available + 0.0001) { toast.error("Stock insuffisant"); return false; }
      updateItem(product.id, { quantity: existing.quantity + qty, unit_price: unitPrice });
    } else {
      const q = Math.min(qty, product.stock);
      setCart([...cart, { product_id: product.id, product_name: product.name, sku: product.sku, quantity: q, qty_raw: String(q), unit_price: unitPrice, price_raw: String(unitPrice), base_price: product.price, total: Math.round(q * unitPrice * 100) / 100, max_stock: product.stock, unit: product.unit }]);
    }
    if (focusQty) { setSearchTerm(""); setFocusTarget({ id: product.id, field: "qty" }); }
    return true;
  };

  // Ligne de saisie : Référence → Quantité → Prix → Ajouter
  const pickPending = (product) => {
    setPending({ product, qty_raw: "1", price_raw: String(product.price) });
    setSearchTerm(product.name);
    setFocusTarget({ field: "pqty" });
  };
  const commitPending = () => {
    if (!pending.product) { toast.error("Choisissez d'abord un article"); return; }
    const q = parseNum(pending.qty_raw);
    if (isNaN(q) || q <= 0) { toast.error("Quantité invalide"); setFocusTarget({ field: "pqty" }); return; }
    const pr = canEditPrices ? parseNum(pending.price_raw) : null;
    if (addToCart(pending.product, false, q, pr)) {
      setPending({ product: null, qty_raw: "", price_raw: "" });
      setSearchTerm("");
      setFocusTarget({ field: "picker" });
    }
  };

  const updateItem = (productId, changes) => {
    setCart(cart.map((item) => {
      if (item.product_id !== productId) return item;
      const next = { ...item, ...changes };
      next.quantity = Math.max(0.01, Math.min(next.quantity, next.max_stock));
      next.unit_price = Math.max(0, next.unit_price);
      next.total = Math.round(next.quantity * next.unit_price * 100) / 100;
      if (changes.quantity !== undefined && changes.qty_raw === undefined) next.qty_raw = String(next.quantity);
      if (changes.unit_price !== undefined && changes.price_raw === undefined) next.price_raw = String(next.unit_price);
      return next;
    }));
  };

  const onQtyChange = (item, raw) => {
    const n = parseNum(raw);
    if (!isNaN(n) && n > 0) updateItem(item.product_id, { quantity: n, qty_raw: raw });
    else setCart(cart.map((c) => (c.product_id === item.product_id ? { ...c, qty_raw: raw } : c)));
  };
  const onPriceChange = (item, raw) => {
    const n = parseNum(raw);
    if (!isNaN(n) && n >= 0) updateItem(item.product_id, { unit_price: n, price_raw: raw });
    else setCart(cart.map((c) => (c.product_id === item.product_id ? { ...c, price_raw: raw } : c)));
  };
  const normalize = (item) => updateItem(item.product_id, { quantity: item.quantity, unit_price: item.unit_price });

  const removeFromCart = (productId) => setCart(cart.filter((item) => item.product_id !== productId));

  const clearLines = () => { setCart([]); setFocusTarget({ field: "picker" }); };
  const resetAll = () => {
    if (editing) { navigate(`/invoice/${editing.id}`); return; }
    setCart([]); setSelectedCustomer("comptoir"); setDiscount(0); setAmountPaid(0); setPaymentMethod("especes"); setCredit(false); setPending({ product: null, qty_raw: "", price_raw: "" }); setSearchTerm("");
    setFocusTarget({ field: "picker" });
  };

  useEffect(() => {
    if (!selectedCustomer || selectedCustomer === "comptoir") { setOverdue(null); return; }
    api.get(`/customers/${selectedCustomer}/overdue-status`).then((r) => setOverdue(r.data.overdue ? r.data : null)).catch(() => setOverdue(null));
  }, [selectedCustomer]);

  const selectedCustomerObj = customers.find((c) => c.id === selectedCustomer);
  const advanceAvailable = Math.max(0, -(selectedCustomerObj?.balance || 0));
  const isAdvance = paymentMethod === "avance";
  const subtotal = cart.reduce((sum, item) => sum + item.total, 0);
  const maxDiscount = Math.floor((subtotal * maxDiscountPct) / 100);
  const total = subtotal - discount;
  const isCreditMode = paymentMethod === "non_paye";
  const isUnpaid = isCreditMode || (!isAdvance && credit && amountPaid <= 0);
  const sentMethod = isUnpaid ? "non_paye" : paymentMethod;
  const effectiveAmountPaid = editing ? editing.amount_paid : isUnpaid ? 0 : isAdvance ? Math.min(advanceAvailable, total) : credit ? Math.min(amountPaid, total) : total;
  const autoAdvance = !editing && !isAdvance && advanceAvailable > 0 && total - effectiveAmountPaid > 0 ? Math.min(advanceAvailable, total - effectiveAmountPaid) : 0;
  const balanceDue = total - effectiveAmountPaid - autoAdvance;
  const discountTooHigh = discount > maxDiscount + 0.01;

  const saveEdit = async () => {
    if (lockedBy) { toast.error(lockedBy); return; }
    setProcessing(true);
    try {
      const customerId = selectedCustomer !== "comptoir" ? selectedCustomer : null;
      await api.put(`/sales/${editing.id}`, {
        items: cart.map(({ product_id, product_name, quantity, unit_price, total }) => ({ product_id, product_name, quantity, unit_price, total })),
        customer_id: customerId,
        customer_name: customerId ? selectedCustomerObj?.name : "Client Comptoir",
        discount,
        notes: editing.notes || "",
        invoice_date: invoiceDate,
        purchase_order_ref: poRef,
        depot_id: depotId || "",
        depot_name: depots.find((d) => d.id === depotId)?.name || "",
      });
      toast.success(`Facture ${editing.invoice_number} modifiée`);
      navigate(`/invoice/${editing.id}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de la modification");
    } finally { setProcessing(false); }
  };

  const handleCheckout = async (managerAuth = null) => {
    if (cart.length === 0) { toast.error("La facture est vide"); return; }
    if (discountTooHigh) { toast.error(`Remise limitée à ${maxDiscountPct}% (${formatCurrency(maxDiscount)})`); return; }
    if (editing) return saveEdit();
    if (isUnpaid && (!selectedCustomer || selectedCustomer === "comptoir")) { toast.error("Sélectionnez un client pour une facture non payée"); return; }
    if (overdue && balanceDue > 0 && !isManager && !managerAuth) { setAuthOpen(true); return false; }
    setProcessing(true);
    try {
      const customerId = selectedCustomer && selectedCustomer !== "comptoir" ? selectedCustomer : null;
      const response = await api.post("/sales", {
        items: cart.map(({ product_id, product_name, quantity, unit_price, total }) => ({ product_id, product_name, quantity, unit_price, total })),
        customer_id: customerId,
        customer_name: customerId ? customers.find((c) => c.id === selectedCustomer)?.name : "Client Comptoir",
        payment_method: sentMethod,
        amount_paid: effectiveAmountPaid,
        discount,
        invoice_date: invoiceDate,
        purchase_order_ref: poRef,
        depot_id: depotId || "",
        depot_name: depots.find((d) => d.id === depotId)?.name || "",
        manager_authorization: managerAuth || undefined,
      });
      if (managerAuth) toast.success("Vente autorisée par le gérant");
      toast.success(`Facture enregistrée: ${response.data.invoice_number}`);
      setAuthOpen(false);
      navigate(`/invoice/${response.data.id}`);
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.code === "OVERDUE_AUTH_REQUIRED") {
        setOverdue({ overdue: true, overdue_amount: detail.overdue_amount, days_late: detail.days_late });
        setAuthOpen(true);
      } else {
        toast.error(typeof detail === "string" ? detail : detail?.message || "Erreur lors de la vente");
      }
      return false;
    } finally { setProcessing(false); }
  };

  const customerName = selectedCustomer === "comptoir" ? "Client Comptoir" : selectedCustomerObj?.name;
  const visibleMethods = PAYMENT_METHODS.filter((m) => m.value !== "avance" || advanceAvailable > 0);
  const currentMethod = PAYMENT_METHODS.find((m) => m.value === paymentMethod) || PAYMENT_METHODS[0];
  const createQuickCustomer = async () => {
    if (!quick.name.trim()) return;
    try {
      const r = await api.post("/customers", { name: quick.name.trim(), phone: quick.phone.trim() });
      setCustomers([...customers, r.data]); setSelectedCustomer(r.data.id); setQuickOpen(false); setQuick({ name: "", phone: "" });
      toast.success(`Client ${r.data.name} créé`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la création du client"); }
  };
  const checkoutDisabled = cart.length === 0 || processing || discountTooHigh || (!editing && isUnpaid && selectedCustomer === "comptoir");

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  const L = ui.labels;
  const money = makeMoney(ui.format);
  const fontClass = FONT_CLASSES[ui.format.fontSize] || "text-sm";
  const dz = DENSITY[ui.format.density] || DENSITY.compact;
  const inp = "h-10 rounded-md bg-white border-[#a9d9c6] focus-visible:ring-[#7fcbb7] text-sm";

  const depotName = depots.find((d) => d.id === depotId)?.name || "";
  const onCardKeyDown = (e) => { if (e.key === "Enter" && e.ctrlKey && !checkoutDisabled) { e.preventDefault(); handleCheckout(); } };

  const lineRow = (item, idx) => (
    <tr key={item.product_id} className="border-b border-[#dcefe6] bg-white hover:bg-[#f3faf7]" data-testid={`invoice-line-${item.product_id}`}>
      <td className={`px-3 ${dz.cell} font-mono text-xs text-slate-500`}>{item.sku || idx + 1}</td>
      <td className={`px-3 ${dz.cell}`}>
        <input type="text" value={item.product_name} onChange={(e) => setCart(cart.map((c) => (c.product_id === item.product_id ? { ...c, product_name: e.target.value } : c)))} onFocus={(e) => e.target.select()} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setFocusTarget({ id: item.product_id, field: "qty" }); } }} title="Désignation modifiable sur cette facture (la fiche article ne change pas)" className="w-full bg-transparent font-medium text-slate-900 leading-tight border-0 border-b border-dashed border-transparent hover:border-teal-400 focus:border-teal-500 focus:outline-none px-0 py-0.5" data-testid={`name-input-${item.product_id}`} />
        {ui.format.density !== "compact" && <p className="text-[11px] text-slate-400 font-mono">stock {item.max_stock}</p>}
      </td>
      <td className={`px-3 ${dz.cell} text-slate-600 text-sm`}>{item.unit || "—"}</td>
      <td className={`px-2 ${dz.cell}`}>
        <Input ref={(el) => { qtyRefs.current[item.product_id] = el; }} type="text" inputMode="decimal" value={item.qty_raw} onChange={(e) => onQtyChange(item, e.target.value)} onBlur={() => normalize(item)} onFocus={(e) => e.target.select()}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (canEditPrices) setFocusTarget({ id: item.product_id, field: "price" }); else setFocusTarget({ field: "picker" }); } }}
          className={`w-full ${dz.input} text-right font-mono rounded-md border-[#a9d9c6] px-2`} title="Quantité (décimales acceptées : 0,5 ; 1,5…)" data-testid={`qty-input-${item.product_id}`} />
      </td>
      <td className={`px-2 ${dz.cell} text-right`}>
        {canEditPrices ? (
          <Input ref={(el) => { priceRefs.current[item.product_id] = el; }} type="text" inputMode="decimal" value={item.price_raw} onChange={(e) => onPriceChange(item, e.target.value)} onBlur={() => normalize(item)} onFocus={(e) => e.target.select()}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setFocusTarget({ field: "picker" }); } }}
            className={`w-full ${dz.input} text-right font-mono rounded-md border-[#a9d9c6] px-2 ${item.unit_price !== item.base_price ? "border-orange-400" : ""}`} data-testid={`price-input-${item.product_id}`} />
        ) : <span className="font-mono">{money(item.unit_price)}</span>}
      </td>
      <td className={`px-3 ${dz.cell} text-right font-mono font-semibold text-slate-900`}>{money(item.total)}</td>
      <td className={`px-2 ${dz.cell} text-right`}>
        <Button variant="ghost" size="sm" tabIndex={-1} onClick={() => removeFromCart(item.product_id)} className="w-7 h-7 p-0 text-red-600" data-testid={`remove-item-${item.product_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
      </td>
    </tr>
  );

  const invoiceCard = (
    <Card className="bg-white border border-[#e4e0d4] rounded-xl shadow-sm" onKeyDown={onCardKeyDown} data-testid="invoice-card">
      <CardContent className="p-5 lg:p-7 space-y-5">
        {/* Bandeau titre + N° document */}
        <div className="flex items-start justify-between gap-4 bg-[#f6f4ee] rounded-lg px-5 py-4" data-testid="invoice-title-band">
          <div>
            <h2 className="font-serif text-2xl text-slate-900 leading-tight">{editing ? "Modification de la facture" : "Facture"}</h2>
            <p className="text-sm text-slate-500 mt-1">{editing ? "Modifiez les lignes, le client ou la remise — les règlements déjà encaissés sont conservés." : "Plusieurs lignes possibles · numéro attribué à la validation"}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[11px] uppercase tracking-wide text-slate-500">N° document</p>
            <p className="font-mono text-xl font-bold text-slate-900" data-testid="invoice-next-number">{editing ? editing.invoice_number : nextNumber || "—"}</p>
          </div>
        </div>

        {/* Dépôt / Client / Téléphone / Date */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600 block mb-1.5">Dépôt</Label>
            <Select value={depotId || "none"} onValueChange={(v) => setDepotId(v === "none" ? "" : v)}>
              <SelectTrigger className={inp} data-testid="invoice-depot-select"><SelectValue placeholder="Dépôt" /></SelectTrigger>
              <SelectContent>{depots.length === 0 && <SelectItem value="none">— Aucun dépôt —</SelectItem>}{depots.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600 block mb-1.5">{L.customer}</Label>
            <div className="flex gap-2">
              <div className="flex-1 min-w-0"><CustomerPicker customers={customers} value={selectedCustomer} onChange={(v) => { setSelectedCustomer(v); if (paymentMethod === "avance") setPaymentMethod("especes"); }} /></div>
              <Button type="button" variant="outline" onClick={() => setQuickOpen(true)} className={`${inp} w-10 px-0 shrink-0`} title="Nouveau client" data-testid="quick-customer-btn"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600 block mb-1.5">Téléphone</Label>
            <Input value={selectedCustomerObj?.phone || ""} readOnly placeholder={selectedCustomer === "comptoir" ? "Client comptoir" : "—"} className={`${inp} bg-[#f3f7f4] text-slate-700`} data-testid="invoice-customer-phone" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-600 block mb-1.5">Date de la vente (jour)</Label>
            <Input type="date" value={invoiceDate} max={toInputDate(new Date())} onChange={(e) => setInvoiceDate(e.target.value)} className={`${inp} font-mono`} data-testid="invoice-date" />
          </div>
        </div>

        {/* Tableau articles */}
        <div className="bg-[#f3f1ea] rounded-lg p-2" data-testid="invoice-lines-wrap">
          <div ref={linesRef} className="max-h-[calc(100vh-560px)] min-h-[120px] overflow-y-auto border border-[#8fcdb6] rounded-md bg-white">
            <table className={`w-full ${fontClass}`} data-testid="invoice-lines-table">
              <thead className="bg-[#eef9f3] text-slate-900 sticky top-0 z-10">
                <tr className="[&>th]:border-r [&>th]:border-[#8fcdb6] [&>th:last-child]:border-r-0 border-b border-[#8fcdb6]">
                  <th className={`px-3 ${dz.head} text-left font-bold w-[16%]`} data-testid="col-ref">Référence</th>
                  <th className={`px-3 ${dz.head} text-left font-bold`} data-testid="col-article">Désignation</th>
                  <th className={`px-3 ${dz.head} text-left font-bold w-[12%]`} data-testid="col-unit">Unité</th>
                  <th className={`px-3 ${dz.head} text-right font-bold w-[13%]`} data-testid="col-qty">Quantité</th>
                  <th className={`px-3 ${dz.head} text-right font-bold w-[13%]`} data-testid="col-price">Prix</th>
                  <th className={`px-3 ${dz.head} text-right font-bold w-[12%]`} data-testid="col-total">Total</th>
                  <th className={`${dz.head} w-[150px]`}></th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-[#f6f4ee] border-b border-[#8fcdb6]" data-testid="entry-row">
                  <td colSpan={2} className="px-2 py-2">
                    <ProductPicker ref={pickerRef} products={products} value={searchTerm} onChange={(v) => { setSearchTerm(v); if (pending.product && v !== pending.product.name) setPending({ product: null, qty_raw: "", price_raw: "" }); }} onPick={pickPending} onTab={focusList} placeholder="Tapez le nom ou la référence de l'article…" className="[&_input]:h-9 [&_input]:rounded-md [&_input]:border-[#a9d9c6] [&_input]:bg-white" />
                  </td>
                  <td className="px-2 py-2">
                    <div className={`h-9 px-3 flex items-center rounded-md border border-[#a9d9c6] bg-white text-sm ${pending.product ? "text-slate-900" : "text-slate-400"}`} data-testid="entry-unit">{pending.product?.unit || "Unité"}</div>
                  </td>
                  <td className="px-2 py-2">
                    <Input ref={pendingQtyRef} type="text" inputMode="decimal" value={pending.qty_raw} placeholder="Quantité" onChange={(e) => setPending({ ...pending, qty_raw: e.target.value })} onFocus={(e) => e.target.select()}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (!pending.product && !pending.qty_raw && cart.length > 0) { handleCheckout(); return; } if (canEditPrices) setFocusTarget({ field: "pprice" }); else commitPending(); } }}
                      className="h-9 rounded-md border-[#a9d9c6] bg-white text-right font-mono" data-testid="entry-qty" />
                  </td>
                  <td className="px-2 py-2">
                    <Input ref={pendingPriceRef} type="text" inputMode="decimal" value={pending.price_raw} placeholder="Prix" readOnly={!canEditPrices} onChange={(e) => setPending({ ...pending, price_raw: e.target.value })} onFocus={(e) => e.target.select()}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitPending(); } }}
                      className={`h-9 rounded-md border-[#a9d9c6] text-right font-mono ${canEditPrices ? "bg-white" : "bg-[#f3f7f4]"}`} data-testid="entry-price" />
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-sm text-slate-700" data-testid="entry-total">{pending.product ? money((parseNum(pending.qty_raw) || 0) * (parseNum(pending.price_raw) || 0)) : ""}</td>
                  <td className="px-2 py-2">
                    <div className="flex gap-1.5 justify-end">
                      <Button type="button" variant="outline" onClick={commitPending} className="h-9 rounded-md bg-white border-[#a9d9c6] text-slate-800 px-3" data-testid="entry-add-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
                      <Button type="button" variant="outline" onClick={() => (winOpen ? setWinOpen(false) : focusList())} className="h-9 w-10 rounded-md bg-white border-[#a9d9c6] px-0" title="Liste des articles (F4)" data-testid="toggle-articles-panel-btn"><PackageSearch className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
                {cart.map(lineRow)}
                {cart.length === 0 && <tr><td colSpan={7} className="text-center py-5 text-slate-500" data-testid="empty-cart">Panier vide</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 px-2 pt-2">Astuce : Entrée ajoute la ligne · Entrée sur quantité vide (ou Ctrl+Entrée) valide la facture · la désignation de chaque ligne est modifiable · F4 ouvre la liste des articles</p>
        </div>

        <div className="border-t border-[#e4e0d4]" />

        {/* Règlement / remise / total */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start" data-testid="payment-section">
          <div className="lg:col-span-5">
            <Label className="text-xs uppercase tracking-wide text-slate-600 block mb-1.5">{L.payment}</Label>
            {editing ? (
              <div className={`h-10 px-3 flex items-center rounded-md border text-xs font-bold uppercase ${paymentButtonClass(editing.payment_method, true)}`} data-testid="edit-mode-notice">{PAYMENT_METHODS.find((m) => m.value === editing.payment_method)?.label || editing.payment_method} · {money(editing.amount_paid)} encaissés</div>
            ) : (
              <PaymentModeButtons value={paymentMethod} onChange={(v) => { setPaymentMethod(v); if (v === "non_paye") { setCredit(false); setAmountPaid(0); } }} methods={visibleMethods} testPrefix="payment" />
            )}
          </div>
          <div className="lg:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-slate-600 block mb-1.5">{L.discount}</Label>
            <Input type="number" value={discount} onChange={(e) => setDiscount(Math.max(0, parseFloat(e.target.value) || 0))} min="0" className={`${inp} font-mono ${discountTooHigh ? "border-red-500" : ""}`} data-testid="input-discount" />
            {discountTooHigh && <p className="text-xs text-red-600 mt-1" data-testid="discount-limit-warning">Max {maxDiscountPct}% : {money(maxDiscount)}</p>}
          </div>
          <div className="lg:col-span-2 lg:pt-7 space-y-2">
            {!editing && !isAdvance && !isCreditMode && (
              <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer select-none h-10">
                <input type="checkbox" checked={credit} onChange={(e) => { setCredit(e.target.checked); setAmountPaid(0); }} className="w-4 h-4 accent-teal-600" data-testid="credit-checkbox" />
                Paiement partiel (acompte)
              </label>
            )}
            {!editing && credit && !isAdvance && !isCreditMode && (
              <div className="flex gap-1 items-center">
                <Label className="text-xs uppercase tracking-wide text-slate-600 whitespace-nowrap">{L.amountPaid}</Label>
                <Input type="number" value={amountPaid} onChange={(e) => setAmountPaid(Math.max(0, parseFloat(e.target.value) || 0))} min="0" className={`${inp} font-mono h-9`} data-testid="input-amount-paid" />
                <Button variant="outline" size="sm" onClick={() => setAmountPaid(total)} className="h-9 text-xs text-teal-700 bg-white whitespace-nowrap rounded-md" data-testid="exact-amount-btn">Exact</Button>
              </div>
            )}
            {!editing && isUnpaid && <p className="text-xs text-red-700 pt-2" data-testid="unpaid-notice">Non payé = vente à crédit : aucun encaissement, le total ({money(total)}) est porté au compte du client{selectedCustomer === "comptoir" ? " — sélectionnez un client." : "."}</p>}
            {!editing && isAdvance && <p className="text-xs text-teal-800" data-testid="advance-pay-notice">Règlement par avance client ({money(effectiveAmountPaid)}) : aucun encaissement.</p>}
            {advanceAvailable > 0 && !editing && !isAdvance && (
              <p className="text-xs text-teal-800" data-testid="advance-notice">Avance disponible : {money(advanceAvailable)}{autoAdvance > 0 ? ` — ${money(autoAdvance)} seront imputés automatiquement.` : ""}</p>
            )}
            {overdue && (
              <div className="p-2 bg-red-50 border border-red-300 rounded-md text-xs text-red-800" data-testid="overdue-block-notice">
                <p className="font-bold flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> Client en retard de paiement — {money(overdue.overdue_amount)} depuis {overdue.days_late} j.</p>
                <p>{isManager ? "Vente à crédit autorisée (gérant), tracée dans l'historique." : "Vente à crédit bloquée : paiement comptant ou autorisation du gérant."}</p>
              </div>
            )}
          </div>
          <div className="lg:col-span-3 text-right space-y-0.5">
            <p className="text-sm text-slate-500">{L.subtotal} <span className="font-mono" data-testid="pos-subtotal">{money(subtotal)}</span></p>
            {discount > 0 && <p className="text-sm text-green-700">Remise <span className="font-mono">-{money(discount)}</span></p>}
            {!editing && credit && <p className="text-sm text-slate-500">{L.amountPaid} <span className="font-mono">{money(effectiveAmountPaid)}</span></p>}
            <p className="font-serif text-4xl text-slate-900 leading-none pt-1"><span data-testid="pos-total">{money(total).replace(/\s?F\s?CFA/, "")}</span> <span className="text-xl text-slate-600">FCFA</span></p>
            {balanceDue > 0 && <p className="text-sm text-orange-600 font-medium">{L.balanceDue} <span className="font-mono" data-testid="balance-due">{money(balanceDue)}</span></p>}
            {balanceDue < 0 && <p className="text-sm text-green-600 font-medium">{editing ? "Excédent (→ avance client)" : "Monnaie à rendre"} <span className="font-mono">{money(Math.abs(balanceDue))}</span></p>}
          </div>
        </div>

        {/* Boutons */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-3" data-testid="invoice-action-buttons">
          <Button variant="outline" onClick={resetAll} className="h-12 rounded-md bg-white border-[#a9d9c6] text-slate-800 text-base" data-testid="cancel-invoice-btn"><XCircle className="w-4 h-4 mr-2" /> {L.cancel}</Button>
          <Button onClick={() => handleCheckout()} disabled={checkoutDisabled} className="h-12 rounded-md text-base text-white bg-[#6cc9bb] hover:bg-[#55b9aa] disabled:bg-[#a9dfd6] disabled:opacity-100" data-testid="checkout-btn">
            {processing ? <div className="spinner w-4 h-4" /> : <><Receipt className="w-4 h-4 mr-2" /> {editing ? L.save : "Valider la facture"}</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="-m-6 lg:-m-8 p-6 lg:p-8 min-h-screen bg-[#f6f4ee] space-y-4" data-testid="pos-page">
      <LockBanner message={lockedBy} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-[Chivo] tracking-tight text-slate-900 flex items-center gap-2">
            {editing ? <><Pencil className="w-5 h-5 text-teal-700" /> MODIFICATION DE LA FACTURE <span className="font-mono text-teal-700">{editing.invoice_number}</span></> : L.title}
          </h1>
          <p className="text-slate-500 mt-1 text-sm">{depotName ? `Dépôt : ${depotName} · ` : ""}Établie par {editing ? editing.cashier_name : user?.name}{cart.length ? ` · ${cart.length} ligne${cart.length > 1 ? "s" : ""}` : ""}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {cashSession === null && (
            <Link to="/cash" className="flex items-center gap-2 px-2 py-1 bg-orange-50 border border-orange-200 text-orange-800 text-xs rounded-sm hover:bg-orange-100 transition-colors" data-testid="pos-cash-closed-banner">
              <Lock className="w-3.5 h-3.5" /> Caisse non ouverte — ouvrir la caisse
            </Link>
          )}
          {cashSession && (
            <span className="flex items-center gap-2 px-2 py-1 bg-green-50 border border-green-200 text-green-800 text-xs rounded-sm" data-testid="pos-cash-open-badge">
              <span className="w-2 h-2 rounded-full bg-green-500" /> Caisse ouverte
            </span>
          )}
          {isAdmin && (
            <Button variant="outline" className="rounded-md bg-white" onClick={() => setCustomizeOpen(true)} data-testid="customize-invoice-ui-btn"><SlidersHorizontal className="w-4 h-4 mr-2" /> Personnaliser l'écran</Button>
          )}
        </div>
      </div>

      <div data-testid="pos-layout">{invoiceCard}</div>

      <ArticlesWindow ref={winRef} open={winOpen} onClose={(refocus = true) => { setWinOpen(false); if (refocus) setFocusTarget({ field: "picker" }); }} products={products} searchTerm={searchTerm} onSearch={setSearchTerm} onPick={(p) => pickPending(p)} onBarcode={handleBarcode} money={money} fontClass={fontClass} title={L.articles} />

      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <DialogContent className="max-w-sm" data-testid="quick-customer-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Nouveau client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="overline">Nom</Label><Input value={quick.name} onChange={(e) => setQuick({ ...quick, name: e.target.value })} autoFocus className="rounded-md h-9" data-testid="quick-customer-name" /></div>
            <div><Label className="overline">Téléphone</Label><Input value={quick.phone} onChange={(e) => setQuick({ ...quick, phone: e.target.value })} onKeyDown={(e) => e.key === "Enter" && createQuickCustomer()} className="rounded-md h-9 font-mono" data-testid="quick-customer-phone" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" className="rounded-md" onClick={() => setQuickOpen(false)}>Annuler</Button><Button className="bg-[#6cc9bb] hover:bg-[#55b9aa] text-white rounded-md" disabled={!quick.name.trim()} onClick={createQuickCustomer} data-testid="quick-customer-submit">Créer</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      <ManagerAuthDialog open={authOpen} onOpenChange={setAuthOpen} overdue={overdue} customerName={customerName} processing={processing} onAuthorize={(auth) => handleCheckout(auth)} />
      {isAdmin && <InvoiceUiCustomizer open={customizeOpen} onOpenChange={setCustomizeOpen} ui={ui} update={updateUi} onSave={() => saveUi()} onReset={resetUi} onCancel={revertUi} />}
    </div>
  );
};

export default POSPage;
